import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { Modal } from "antd";

const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN_LEFT = 40;
const MARGIN_RIGHT = 40;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_LEFT - MARGIN_RIGHT;

const COLORS = {
  black: rgb(0, 0, 0),
  gray: rgb(0.4, 0.4, 0.4),
  lightGray: rgb(0.85, 0.85, 0.85),
  white: rgb(1, 1, 1),
  headerBg: rgb(0.2, 0.2, 0.2),
};

export const valueLabels = {
  "s1": "S1", "s3": "S3", "s4": "S4", "s6": "S6", "s9": "S9", "s12": "S12",
  "never": "Jamais",
  "less-than-once": "Moins d'une fois par semaine",
  "once": "1 fois par semaine",
  "twice": "2 fois par semaine",
  "three": "3 fois par semaine",
  "four": "4 fois par semaine",
  "five": "5 fois par semaine",
  "six": "6 fois par semaine",
  "seven": "7 fois par semaine",
  "alone": "Seul(e)",
  "with-caregiver": "Avec proche aidant",
  "caregiver-supervision": "Sous la supervision du proche aidant",
  "with-friends": "Avec ami.e.s",
  "very-easy": "Très facile",
  "easy": "Facile",
  "hard": "Difficile",
  "very-hard": "Très difficile",
  "no-pain": "Aucune douleur",
  "light-pain": "Douleur légère",
  "moderate-pain": "Douleur modérée",
  "severe-pain": "Douleur sévère",
  "sometimes": "Parfois",
  "often": "Souvent",
  "always": "Toujours",
  "very-unsatisfied": "Très insatisfait",
  "unsatisfied": "Insatisfait",
  "satisfied": "Satisfait",
  "very-satisfied": "Très satisfait",
  "doesnt-do": "Ne fait pas cet exercice",
  "level-1": "(1*)",
  "level-2": "(2*)",
  "level-3": "(3*)",
  "effort-linked": "Douleur normale liée à l'effort",
  "arthritis": "Arthrose - Arthrite",
  "tendinopathy": "Tendinopathie",
  "algic": "Patient algique",
  "secondary-effect": "Effet secondaire médicaments",
  "other": "Autre",
  "can-talk-easily": "Peut aisément parler",
  "difficulty-talking": "Peut difficilement parler",
  "cant-talk": "Ne peut pas parler",
  "none": "Aucune",
  "perform-easy-only": "Pratique seulement les adaptations faciles (1*)",
  "decrease-repetitions": "Diminution du nombre de répétitions",
  "increase-rest": "Augmentation du temps de repos entre les séries",
  "decrease-sets": "Diminution du nombre de séries",
  "remove-one-exercise": "Suppression d'un exercice du programme",
  "remove-two-exercises": "Suppression de deux exercices du programme",
};

const fmt = (value) => {
  if (value === true) return "Oui";
  if (value === false) return "Non";
  if (value === null || value === undefined || value === "") return "—";
  return valueLabels[value] || String(value);
};

const levelLabel = (level) => {
  const levels = { 1: "Niveau I", 2: "Niveau II", 3: "Niveau III", 4: "Niveau IV", 5: "Niveau V" };
  return levels[level] || String(level);
};

class PdfBuilder {
  constructor(pdfDoc, font, fontBold) {
    this.pdfDoc = pdfDoc;
    this.font = font;
    this.fontBold = fontBold;
    this.page = null;
    this.y = 0;
    this.pageNum = 0;
  }

  newPage() {
    this.page = this.pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
    this.y = PAGE_HEIGHT - 40;
    this.pageNum++;
    // Page number
    this.page.drawText(`PACE - Page ${this.pageNum}`, {
      x: PAGE_WIDTH - 100, y: PAGE_HEIGHT - 25, size: 8, font: this.font, color: COLORS.gray,
    });
  }

  checkSpace(needed) {
    if (this.y - needed < 50) {
      this.newPage();
    }
  }

  drawTitle(text) {
    this.checkSpace(35);
    this.page.drawText(text, {
      x: MARGIN_LEFT, y: this.y, size: 16, font: this.fontBold, color: COLORS.black,
    });
    this.y -= 25;
  }

  drawSectionHeader(text) {
    this.checkSpace(30);
    this.page.drawRectangle({
      x: MARGIN_LEFT, y: this.y - 5, width: CONTENT_WIDTH, height: 20,
      color: COLORS.headerBg,
    });
    this.page.drawText(text, {
      x: MARGIN_LEFT + 8, y: this.y, size: 10, font: this.fontBold, color: COLORS.white,
    });
    this.y -= 25;
  }

  drawField(label, value, indent = 0) {
    this.checkSpace(18);
    const x = MARGIN_LEFT + indent;
    const labelText = `${label} : `;
    const labelWidth = this.fontBold.widthOfTextAtSize(labelText, 9);
    this.page.drawText(labelText, {
      x, y: this.y, size: 9, font: this.fontBold, color: COLORS.black,
    });
    this.page.drawText(fmt(value), {
      x: x + labelWidth, y: this.y, size: 9, font: this.font, color: COLORS.gray,
    });
    this.y -= 15;
  }

  drawFieldWrapped(label, value, indent = 0) {
    this.checkSpace(18);
    const x = MARGIN_LEFT + indent;
    const labelText = `${label} : `;
    this.page.drawText(labelText, {
      x, y: this.y, size: 9, font: this.fontBold, color: COLORS.black,
    });
    this.y -= 14;

    const text = fmt(value);
    const maxWidth = CONTENT_WIDTH - indent - 10;
    const lines = this.wrapText(text, 9, maxWidth);
    for (const line of lines) {
      this.checkSpace(14);
      this.page.drawText(line, {
        x: x + 10, y: this.y, size: 9, font: this.font, color: COLORS.gray,
      });
      this.y -= 13;
    }
  }

  drawSeparator() {
    this.checkSpace(10);
    this.page.drawLine({
      start: { x: MARGIN_LEFT, y: this.y },
      end: { x: PAGE_WIDTH - MARGIN_RIGHT, y: this.y },
      thickness: 0.5, color: COLORS.lightGray,
    });
    this.y -= 10;
  }

  drawSpacing(px = 8) {
    this.y -= px;
  }

  wrapText(text, size, maxWidth) {
    const words = text.split(' ');
    const lines = [];
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (this.font.widthOfTextAtSize(test, size) > maxWidth) {
        if (current) lines.push(current);
        current = word;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }
}

export const exportFollowUpPacePdf = async (followUp, patientId) => {
  try {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const b = new PdfBuilder(pdfDoc, font, fontBold);

    // ===== PAGE 1 =====
    b.newPage();
    b.drawTitle("Appels de suivi - vague PACE");
    b.drawSeparator();

    // Informations du patient
    b.drawSectionHeader("Informations du patient");
    b.drawField("Code participant", patientId);
    b.drawField("Date du jour", followUp.currentDate);
    b.drawField("Nom du kinésiologue", followUp.kinesologistName);
    b.drawField("Type d'évaluation", fmt(followUp.evaluationType));
    b.drawSeparator();

    // Santé et programme
    b.drawSectionHeader("Santé et programme");
    b.drawField("État de santé (1-10)", followUp.selfHealthRating);
    b.drawField("Niveau de difficulté du programme", levelLabel(followUp.programDifficultyLevel));
    b.drawField("Fréquence moyenne des exercices prescrits", fmt(followUp.averagePrescribedExerciseDone));
    b.drawField("Pratique de la prescription", fmt(followUp.exercisePrescriptionPracticeWay));
    b.drawField("Opinion sur le programme d'exercices", fmt(followUp.prescripedExerciseOpinion));
    b.drawSeparator();

    // Difficulté par exercice
    b.drawSectionHeader("Difficulté par exercice");
    b.drawField("Exercice 1", fmt(followUp.exercise1Difficulty));
    b.drawField("Exercice 2", fmt(followUp.exercise2Difficulty));
    b.drawField("Exercice 3", fmt(followUp.exercise3Difficulty));
    b.drawField("Exercice 4", fmt(followUp.exercise4Difficulty));
    b.drawSeparator();

    // Satisfaction et douleur
    b.drawSectionHeader("Satisfaction et douleur");
    b.drawField("Satisfaction des séances d'AP", fmt(followUp.apSatisfactionLevel));
    b.drawField("Douleur lors du programme", fmt(followUp.programPainLevel));
    if (followUp.frequencyOfPain) {
      b.drawField("Fréquence de la douleur", fmt(followUp.frequencyOfPain));
    }
    if (followUp.originOfPain) {
      b.drawField("Origine de la douleur", fmt(followUp.originOfPain));
    }
    if (followUp.modificationToAddress) {
      b.drawField("Modification(s) apportée(s)", fmt(followUp.modificationToAddress));
    }
    b.drawSeparator();

    // Sécurité
    b.drawSectionHeader("Sécurité");
    b.drawField("Sentiment de sécurité", fmt(followUp.feelingOfSafety));
    if (followUp.feelingOfSafety === false && followUp.safetyFeelWhy) {
      b.drawFieldWrapped("Raison", followUp.safetyFeelWhy);
    }
    if (followUp.feelingOfSafety === false && followUp.safetyFeelCorrection) {
      b.drawFieldWrapped("Stratégie proposée", followUp.safetyFeelCorrection);
    }
    b.drawSeparator();

    // Marche
    b.drawSectionHeader("Marche");
    b.drawField("A marché durant les dernières semaines", fmt(followUp.hasWalkedPastWeeks));
    if (followUp.hasWalkedPastWeeks === true) {
      if (followUp.hasWalkedFrequency) {
        b.drawField("Fréquence de marche par semaine", fmt(followUp.hasWalkedFrequency));
      }
      if (followUp.hasWalkedAverage) {
        b.drawField("Temps moyen de marche par jour", `${followUp.hasWalkedAverage} min.`);
      }
      if (followUp.walkingPrescriptionPracticeWay) {
        b.drawField("Pratique de la marche", fmt(followUp.walkingPrescriptionPracticeWay));
      }
    }
    b.drawSeparator();

    // Chutes
    b.drawSectionHeader("Chutes");
    b.drawField("A chuté durant les dernières semaines", fmt(followUp.hasFallenPastWeeks));
    if (followUp.hasFallenPastWeeks === true) {
      if (followUp.timesFallenCount) {
        b.drawField("Nombre de chutes", followUp.timesFallenCount);
      }
      if (followUp.fallCircumstances) {
        b.drawFieldWrapped("Circonstances", followUp.fallCircumstances);
      }
    }
    b.drawSeparator();

    // Motivation
    b.drawSectionHeader("Motivation");
    b.drawField("Motivé(e) pour la prescription d'AP", fmt(followUp.prescripedApMotivation));
    if (followUp.prescripedApMotivation === false && followUp.notMotivatedReason) {
      b.drawFieldWrapped("Raison", followUp.notMotivatedReason);
    }
    b.drawSeparator();

    // Autre activité physique
    b.drawSectionHeader("Autre activité physique");
    b.drawField("Autre activité intégrée", fmt(followUp.otherActivityIntegration));
    if (followUp.otherActivityIntegration === true) {
      if (followUp.additionalActivity1Name) b.drawField("Activité 1", followUp.additionalActivity1Name, 10);
      if (followUp.additionalActivity1Frequency) b.drawField("Fréquence", fmt(followUp.additionalActivity1Frequency), 10);
      if (followUp.additionalActivity1Length) b.drawField("Durée (min)", followUp.additionalActivity1Length, 10);
      if (followUp.additionalActivity1Intensity) b.drawField("Intensité", fmt(followUp.additionalActivity1Intensity), 10);
    }
    if (followUp.otherActivityIntegration === true && followUp.anotherActivityIntegration === true) {
      b.drawSpacing(5);
      if (followUp.additionalActivity2Name) b.drawField("Activité 2", followUp.additionalActivity2Name, 10);
      if (followUp.additionalActivity2Frequency) b.drawField("Fréquence", fmt(followUp.additionalActivity2Frequency), 10);
      if (followUp.additionalActivity2Length) b.drawField("Durée (min)", followUp.additionalActivity2Length, 10);
      if (followUp.additionalActivity2Intensity) b.drawField("Intensité", fmt(followUp.additionalActivity2Intensity), 10);
    }
    if (followUp.otherActivityIntegration === true && followUp.anotherActivityIntegration === true && 
      followUp.yetAnotherActivityIntegration === true) {
      b.drawSpacing(5);
      if (followUp.additionalActivity3Name) b.drawField("Activité 3", followUp.additionalActivity3Name, 10);
      if (followUp.additionalActivity3Frequency) b.drawField("Fréquence", fmt(followUp.additionalActivity3Frequency), 10);
      if (followUp.additionalActivity3Length) b.drawField("Durée (min)", followUp.additionalActivity3Length, 10);
      if (followUp.additionalActivity3Intensity) b.drawField("Intensité", fmt(followUp.additionalActivity3Intensity), 10);
    }
    b.drawSeparator();

    // Consultation
    b.drawSectionHeader("Consultation");
    b.drawField("A vu un médecin", fmt(followUp.hasSeenDoctor));
    if (followUp.hasSeenDoctor === true) {
      if (followUp.timesVisitedDoctorCount) b.drawField("Nombre de visites", followUp.timesVisitedDoctorCount, 10);
      if (followUp.doctorVisitReasons) b.drawFieldWrapped("Raison", followUp.doctorVisitReasons, 10);
    }
    b.drawField("Hôpital / chirurgie d'un jour", fmt(followUp.hasBeenToHospital));
    if (followUp.hasBeenToHospital === true) {
      if (followUp.timesVisitedHospitalCount) b.drawField("Nombre de visites", followUp.timesVisitedHospitalCount, 10);
      if (followUp.hospitalVisitReasons) b.drawFieldWrapped("Raison", followUp.hospitalVisitReasons, 10);
    }
    b.drawField("Autre professionnel de santé", fmt(followUp.hasSeenOtherProfessional));
    if (followUp.hasSeenOtherProfessional === true) {
      if (followUp.timesVisitedSpecialistCount) b.drawField("Nombre de visites", followUp.timesVisitedSpecialistCount, 10);
      if (followUp.specialistVisitReasons) b.drawFieldWrapped("Raison", followUp.specialistVisitReasons, 10);
    }

    // Save and download
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const evalType = (followUp.evaluationType || "suivi").toUpperCase();
    link.download = `${followUp.currentDate || "suivi"}_${patientId}_FollowUp_${evalType}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('Erreur génération PDF suivi:', error);
    Modal.error({
      title: 'Erreur',
      content: 'Erreur lors de la génération du PDF: ' + (error.message || 'Erreur inconnue'),
    });
  }
};
